import React, { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Loader2, CheckCircle2, Clock, RefreshCw, AlertTriangle } from 'lucide-react';

// Who has said they have their route, and who has not.
//
// route_acceptance_status() has existed since 20261035 and returned exactly
// this — it was simply never called from anywhere, so the office had no way to
// see an unaccepted route except by waiting for the depot to ring. The chase
// job read the same data and messaged the crew; nobody showed it to the admin.
//
// Names on BOTH sides, not just counts: "3 of 4" tells you there is a problem,
// "Silent Sam" tells you who to ring, and ringing them is the actual next step.

const fmtWhen = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      weekday: 'short', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const RouteAcceptanceModal = ({ open, onOpenChange, days = 3 }) => {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase.rpc('route_acceptance_status', { p_days: days });
      if (err) throw err;
      setRows(data || []);
    } catch (e) {
      // An empty list and a failed query look identical if the error is
      // swallowed — and "nothing outstanding" is the reassuring reading.
      setError(e?.message || String(e));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const outstanding = (rows || []).filter((r) => (r.accepted ?? 0) < (r.recipients ?? 0));
  const settled = (rows || []).filter((r) => (r.accepted ?? 0) >= (r.recipients ?? 0));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Route acceptance
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={load} disabled={loading}
                    aria-label="Refresh">
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </DialogTitle>
          <DialogDescription>
            Routes sent in the last {days} day{days === 1 ? '' : 's'} and whether the crew has confirmed they have them.
          </DialogDescription>
        </DialogHeader>

        {error && (
          <div className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 p-3 text-xs text-rose-700 dark:border-rose-900 dark:bg-rose-950/30 dark:text-rose-300">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              <p className="font-semibold">Could not read acceptance state</p>
              <p className="mt-0.5">{error}</p>
              <p className="mt-1 italic">This is not the same as nothing being outstanding.</p>
            </div>
          </div>
        )}

        {loading && rows === null && (
          <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        )}

        {rows !== null && rows.length === 0 && !error && (
          <p className="py-8 text-center text-sm text-muted-foreground">
            No routes have been sent for acceptance in the last {days} day{days === 1 ? '' : 's'}.
          </p>
        )}

        {outstanding.length > 0 && (
          <section className="space-y-2">
            <h3 className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-amber-600">
              <Clock className="h-3.5 w-3.5" /> Awaiting acceptance ({outstanding.length})
            </h3>
            {outstanding.map((r) => (
              <div key={r.message_id} className="rounded-xl border border-amber-200 bg-amber-50/60 p-3 dark:border-amber-900/50 dark:bg-amber-950/20">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="font-semibold text-sm text-foreground">
                    {r.crew_identifier || 'Crew'} · {r.allocation_date || '—'}
                    {r.allocation_type && r.allocation_type !== 'route' && (
                      <Badge variant="outline" className="ml-2 text-[10px] uppercase">{r.allocation_type}</Badge>
                    )}
                  </div>
                  <Badge className="bg-amber-600 text-[10px] font-bold text-white">
                    {r.accepted ?? 0} of {r.recipients ?? 0}
                  </Badge>
                </div>
                <p className="mt-1.5 text-xs text-amber-800 dark:text-amber-300">
                  <span className="font-semibold">Not yet accepted:</span> {r.outstanding_names || '—'}
                </p>
                {r.accepted_names && (
                  <p className="mt-0.5 text-xs text-muted-foreground">Accepted: {r.accepted_names}</p>
                )}
                <p className="mt-1 text-[10px] uppercase tracking-widest text-muted-foreground/70">
                  Sent {fmtWhen(r.sent_at)}
                </p>
              </div>
            ))}
          </section>
        )}

        {settled.length > 0 && (
          <section className="space-y-2 pt-2">
            <h3 className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-emerald-600">
              <CheckCircle2 className="h-3.5 w-3.5" /> Accepted ({settled.length})
            </h3>
            {settled.map((r) => (
              <div key={r.message_id} className="rounded-xl border border-border bg-background/50 p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="text-sm font-semibold text-foreground">
                    {r.crew_identifier || 'Crew'} · {r.allocation_date || '—'}
                  </div>
                  <Badge className="bg-emerald-600 text-[10px] font-bold text-white">
                    {r.accepted ?? 0} of {r.recipients ?? 0}
                  </Badge>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">{r.accepted_names}</p>
              </div>
            ))}
          </section>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RouteAcceptanceModal;
